// Angular
import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewEncapsulation, ViewRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
// RxJS
import { Subject } from 'rxjs';
// Translate
import { TranslateService } from '@ngx-translate/core';
// Auth
import { AuthNoticeService } from '../../../../core/auth';
import { ChangePasswordService } from 'app/core/auth/password-governance/_services/change-password.service';
import { IAlert } from '../../ngbootstrap/alert/alert.component';
import { LayoutUtilsService } from 'app/core/_base/crud';
import { finalize } from 'rxjs/operators';

@Component({
  standalone: false,
    selector: 'kt-forgot-password',
    templateUrl: './forgot-password.component.html',
    styleUrls: ['./forgot-password.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
	isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    /**
     * Data alert
     */
    public alert: IAlert;

    /**
     * Variable indicating if the alert is displayed
     */
    public showedAlert = false;

    // Public params
    forgotPasswordForm: UntypedFormGroup;
    loading = false;
    errors: any = [];

    private readonly unsubscribe = new Subject<void>(); // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

    /**
     * Component constructor
     *
     * @param authService
     * @param authNoticeService
     * @param translate
     * @param router
     * @param fb
     * @param cdr
     */
    constructor(
        private changePasswordService: ChangePasswordService,
        public authNoticeService: AuthNoticeService,
        private layoutUtilsService: LayoutUtilsService,
        private translate: TranslateService,
        private router: Router,
        private fb: UntypedFormBuilder,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
    }

    /**
     * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
     */

    /**
     * On init
     */
    ngOnInit() {
        this.initRegistrationForm();
		this.isPortrait();
    }
	@HostListener('window:orientationchange', ['$event'])
	onOrientationChange(event) {
		localStorage.setItem('isPortrait',window.orientation == 0? "true":"false");

		this.updateDOM();
	}

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this.unsubscribe.next(undefined);
        this.unsubscribe.complete();
        this.loading = false;
    }
	isPortrait() {
		localStorage.setItem('isPortrait',window.innerHeight > window.innerWidth ? "true":"false");

	}
	getItem ( item ) {
	   return localStorage.getItem(item)
	}

    /**
     * Form initalization
     * Default params, validators
     */
    initRegistrationForm() {
        this.forgotPasswordForm = this.fb.group({
            email: ['', Validators.compose([
                Validators.required,
                Validators.email,
                Validators.minLength(3),
                Validators.maxLength(320)
            ])
            ],
            username: ['', Validators.compose([
                Validators.required,
                Validators.minLength(3),
                Validators.maxLength(320)
            ])
            ]
        });
    }

    /**
     * Checks if theres a new change in the dom to be forced
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2020-03-20
     */
    public updateDOM() {
        if (this.changeDetectorRef && !(this.changeDetectorRef as ViewRef).destroyed) {
            this.changeDetectorRef.detectChanges();
        }
    }

    /**
     * Showing general message in alert/dialog window
     * @param message string to show
     */
    private showModalAlert(message: string) {
        const modalRef = this.layoutUtilsService.generalAlert();
        modalRef.componentInstance.data = message;
    }

    /**
     * Form Submit
     */
    submit() {
        const controls = this.forgotPasswordForm.controls;
        /** check form */
        if (this.forgotPasswordForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );
            return;
        }

        this.loading = true;

        const username = controls.username.value;
        const email = controls.email.value;

        this.changePasswordService.validateEmail(username, email).pipe(
            finalize(() => {
                this.loading = false;
                this.updateDOM();
            })
        ).subscribe(
            data => {
                if (data) {
                    this.closeAlert();
                    this.changePasswordService.sendEmailChangePassword(username, email).subscribe(
                        mail => {
                            this.router.navigate(['/auth/login']);
                            this.showModalAlert(mail);
                        }
                    );
                } else {
                    this.showAlert('danger', this.translate.instant('AUTH.FORGOT.WRONG_PARAM'));
                }
            }
        );
    }

    /**
     * Checking control validation
     *
     * @param controlName: string => Equals to formControlName
     * @param validationType: string => Equals to valitors name
     */
    isControlHasError(controlName: string, validationType: string): boolean {
        const control = this.forgotPasswordForm.controls[controlName];
        if (!control) {
            return false;
        }

        const result =
            control.hasError(validationType) &&
            (control.dirty || control.touched);
        return result;
    }

    /**
     * Methode that show the alerts
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-06-18
     */
    private showAlert(typeToSend: string, messageToSend: string) {
        this.alert = {
            id: 1,
            type: typeToSend,
            message: messageToSend,
        };
        this.showedAlert = true;
    }

    /**
     * Methode that close the alerts
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-06-18
     */
    public closeAlert() {
        this.showedAlert = false;
    }
}
